// The quote builder's API client (apps/server/src/quotes.mjs). Every write
// answers with the whole builder payload, repriced.
import { ObjectsApiError } from '../objects/objectsApi';
import type { PricingPolicy } from '@quote-math/pricingAdvice';
import type { RampPeriod } from './ramp';

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

export type DiscountType = 'percent' | 'amount' | 'target';

export interface ScopeRow {
  id: string;
  included: boolean;
  theme: string;
  covers: string;
  stories: string;
  products: { code: string; quantity: number }[];
}

export interface QuoteScope {
  summary: string;
  rows: ScopeRow[];
}

/** The quote's documents, each saved whole. Value, timeline and proposal take shape in later rounds. */
export interface QuoteDocuments {
  scope: QuoteScope | null;
  value_model: Record<string, unknown> | null;
  price_recommendation: Record<string, unknown> | null;
  timeline: Record<string, unknown> | null;
  proposal: Record<string, unknown> | null;
}

export interface QuoteOption {
  id: string;
  quote_number: string;
  name: string | null;
  status: string;
  is_primary: boolean;
  one_time_list: number | null;
  one_time_net: number | null;
  monthly_list: number | null;
  monthly_net: number | null;
}

export interface BuilderQuote extends QuoteDocuments {
  id: string;
  quote_number: string;
  name: string | null;
  kind: 'new' | 'change_order';
  status: string;
  account_id: string;
  account_name: string;
  opportunity_id: string | null;
  opportunity_name: string | null;
  /** Price Grid values chosen on the quote, by "object.field" (e.g. account.company_size); each overrides the account. */
  grid_overrides?: Record<string, string>;
  /** The account's Company Size (key and label), from its Annual Revenue. */
  company_size?: string | null;
  company_size_label?: string | null;
  annual_revenue?: number | null;
  /** When its lines start, and its term lines' length in months, unless a line says otherwise. */
  start_date?: string | null;
  term_months?: number | null;
  one_time_discount_type: DiscountType | null;
  one_time_discount_value: number | null;
  monthly_discount_type: DiscountType | null;
  monthly_discount_value: number | null;
  monthly_discount_months: number | null;
  is_primary: boolean;
}

export interface BuilderLine {
  id: string;
  product_id: string | null;
  product_code: string | null;
  family: string | null;
  /** single, or bundle (a product whose options are its components). */
  structure?: 'single' | 'bundle' | null;
  product_description: string | null;
  group_name: string | null;
  group_sort: number | null;
  /** Estimated hours behind an hourly line (internal). */
  hours: number | null;
  name: string;
  billing: 'one_time' | 'monthly';
  quantity: number;
  list_unit_price: number;
  discount_pct: number;
  discount_months: number | null;
  inclusion: 'workstream' | 'complimentary' | null;
  replaces_asset_id: string | null;
  customer_note: string | null;
  list: number;
  net: number;
  included: boolean;
  /** The bundle line this option belongs to. */
  parent_line_id: string | null;
  /** An option under a fixed-fee bundle: scope, not priced. */
  scope: boolean;
  /** What the volume tiers took off the list. */
  volumeDiscount: number;
  /** The volume tiers snapshotted when the line was added. */
  price_adjustment: PriceAdjustment | null;
  selling_model_id: number | null;
  selling_model: string | null;
  /** Set when the line's revenue schedule was edited by hand on the opportunity; regenerating keeps it. */
  schedule_edited_at?: string | null;
  schedule_edited_by?: string | null;
  /** A term line's length in whole months; null for one-time and evergreen lines. */
  term_months: number | null;
  /** Its price's period as quoted: a price per year counts a twelfth a month. */
  price_period?: 'month' | 'year' | null;
  /** What it comes to over its life: one-time net, or a term line's monthly net × its term; null for evergreen. */
  total?: number | null;
  selling_model_kind?: 'one_time' | 'term_defined' | 'evergreen' | null;
  /** A ramped term line's periods, in order (0085): each so many months at its own quantity and price. */
  ramp_periods?: RampPeriod[];
  /** The line's own service dates (YYYY-MM-DD); null: the quote timeline. */
  start_date: string | null;
  end_date: string | null;
  /** The price the line was quoted on, and its scheme and label. */
  price_id?: string | null;
  price_scheme?: PriceScheme | null;
  price_label?: string | null;
  /** A grid price's cell, as quoted. */
  price_grid_cell_id?: string | null;
  price_grid_cell_snapshot?: GridCellSnapshot | null;
  /** The cell is the whole line (its grid prices the quantity). */
  prices_quantity?: boolean;
  /** What the quantity counts: Hours, the grid's quantity dimension, or Quantity. */
  quantity_label?: string;
  /** A treatment chosen for the line; null: its product's policy default. */
  billing_treatment_id?: string | null;
  billing_treatment_snapshot?: { id: string; name: string; source: 'default' | 'line' } | null;
}

export type PriceScheme = 'flat' | 'per_hour' | 'grid' | 'included';

export interface GridCellSnapshot {
  cell_id: string;
  grid_id: string;
  grid: string;
  keys: string[];
  labels: string[];
  label: string | null;
  amount: number;
  hours: number;
  prices_quantity: boolean;
}

/** One of a product's current prices, from the catalog (a grid price once). */
export interface PriceOption {
  id: string;
  product_id: string;
  label: string;
  scheme: PriceScheme;
  selling_model_id: number;
  amount: number;
  price_grid_id: string | null;
  is_default: boolean;
  period?: 'month' | 'year' | null;
}

/** A Price Grid: one or two dimensions and a cell (fixed amount and hours) per combination. */
export interface PriceGrid {
  id: string;
  name: string;
  dimensions: { object: string; field: string; label: string; values?: { key: string; label: string }[] }[];
  cells: { id: string; key_1: string; key_2: string | null; amount: number; hours: number; label: string | null }[];
}

export interface CompanySize {
  key: string;
  label: string;
}

export interface BillingTreatment {
  id: string;
  name: string;
  method: string;
  billing_timing: string | null;
  billing_frequency: string | null;
  policy: string;
}

/** How a product can be sold: once, for a term, or until cancelled. */
export interface SellingModel {
  id: number;
  name: string;
  kind: 'one_time' | 'term_defined' | 'evergreen';
  /** The default term in months for a term model; null otherwise. */
  term_months: number | null;
}

export interface PriceAdjustment {
  method: 'range' | 'slab';
  tiers: { lower: number; upper: number | null; type: 'percent' | 'amount' | 'override'; value: number }[];
}

/** One month (or other period) of a commercial line's revenue and hours. */
export interface RevenueScheduleRow {
  id: string;
  quote_line_id: string;
  period_start: string;
  period_end: string;
  amount: number;
  hours: number | null;
}

export interface BuilderTotals {
  oneTime: { list: number; included: number; pm: number; lineDiscount: number; discount: number; net: number };
  monthly: { list: number; lineDiscount: number; discount: number; net: number; current: number; replaced: number; after: number };
  firstYear: { discount: number; grandTotal: number };
}

export interface QuoteBuilder {
  quote: BuilderQuote;
  lines: BuilderLine[];
  totals: BuilderTotals;
  assets: { id: string; name: string; monthly: number }[];
  companySizes?: CompanySize[];
  billingTreatments?: BillingTreatment[];
  options: QuoteOption[];
  policy: PricingPolicy | null;
  schedules: RevenueScheduleRow[];
}

/** A bundle product's option, from the catalog. */
export interface BundleOption {
  id: string;
  bundle_product_id: string;
  option_product_id: string;
  option_group: string | null;
  required: boolean;
  default_quantity: number | null;
  included: boolean;
}

export interface CatalogProduct {
  id: string;
  code: string;
  name: string;
  /** One-time or monthly, from its default price's selling model; null with no price. */
  billing: 'one_time' | 'monthly' | null;
  structure: 'single' | 'bundle';
  group_name: string;
  family: string;
  description: string | null;
  size: 'XS' | 'S' | 'M' | 'L' | 'XL' | null;
  sort_order: number;
  group_code: string;
  group_description: string | null;
  group_required: boolean;
  group_sort: number;
}

async function payload<T>(response: Response, fallback: string): Promise<T> {
  if (!response.ok) {
    let message = fallback;
    try {
      const body: unknown = await response.json();
      if (body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string') message = (body as { error: string }).error;
    } catch {
      // keep the fallback
    }
    throw new ObjectsApiError(response.status, message);
  }
  return (await response.json()) as T;
}

const json = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
});
const base = (id: string) => `/api/quotes/${encodeURIComponent(id)}`;

export async function fetchBuilder(fetcher: Fetch, id: string): Promise<QuoteBuilder> {
  return payload(await fetcher(`${base(id)}/builder`), 'Could not load the quote.');
}

export interface Catalog {
  products: CatalogProduct[];
  bundleOptions: BundleOption[];
  sellingModels: SellingModel[];
  priceOptions: PriceOption[];
  priceGrids: PriceGrid[];
}

export async function fetchCatalog(fetcher: Fetch): Promise<Catalog> {
  const body = await payload<Partial<Catalog> & { products: CatalogProduct[] }>(await fetcher('/api/cpq/catalog'), 'Could not load the catalog.');
  return {
    products: body.products,
    bundleOptions: body.bundleOptions ?? [],
    sellingModels: body.sellingModels ?? [],
    priceOptions: body.priceOptions ?? [],
    priceGrids: body.priceGrids ?? [],
  };
}

export async function savePricing(fetcher: Fetch, id: string, body: Partial<BuilderQuote>): Promise<QuoteBuilder> {
  return payload(await fetcher(`${base(id)}/pricing`, json('PATCH', body)), 'Could not save the pricing.');
}

/** Adds a product; with `parentLineId`, as an option of that bundle line. */
export async function addLine(
  fetcher: Fetch, id: string, productId: string, { parentLineId, quantity }: { parentLineId?: string; quantity?: number } = {},
): Promise<QuoteBuilder> {
  const body = { product_id: productId, ...(parentLineId ? { parent_line_id: parentLineId } : {}), ...(quantity ? { quantity } : {}) };
  return payload(await fetcher(`${base(id)}/lines`, json('POST', body)), 'Could not add the product.');
}

/** Rebuilds every commercial line's revenue schedule from the timeline. */
export async function generateSchedules(fetcher: Fetch, id: string): Promise<QuoteBuilder> {
  return payload(await fetcher(`${base(id)}/schedules`, { method: 'POST' }), 'Could not make the revenue schedule.');
}

/** Replaces a term line's ramp (its periods in order); no periods unramps it. */
export async function saveRamp(fetcher: Fetch, id: string, lineId: string, body: { periods: { months: number; quantity: number; price_grid_cell_id?: string }[] }): Promise<QuoteBuilder> {
  return payload(await fetcher(`${base(id)}/lines/${encodeURIComponent(lineId)}/ramp`, json('PUT', body)), 'Could not save the ramp.');
}

export async function saveLine(fetcher: Fetch, id: string, lineId: string, body: Record<string, unknown>): Promise<QuoteBuilder> {
  return payload(await fetcher(`${base(id)}/lines/${encodeURIComponent(lineId)}`, json('PATCH', body)), 'Could not save the line.');
}

export async function removeLine(fetcher: Fetch, id: string, lineId: string): Promise<QuoteBuilder> {
  return payload(await fetcher(`${base(id)}/lines/${encodeURIComponent(lineId)}`, { method: 'DELETE' }), 'Could not remove the line.');
}

export async function saveDocuments(fetcher: Fetch, id: string, body: Partial<QuoteDocuments>): Promise<QuoteBuilder> {
  return payload(await fetcher(`${base(id)}/documents`, json('PATCH', body)), 'Could not save.');
}

export async function duplicateQuote(fetcher: Fetch, id: string): Promise<string> {
  return (await payload<{ id: string }>(await fetcher(`${base(id)}/duplicate`, { method: 'POST' }), 'Could not copy the quote.')).id;
}

export async function makePrimary(fetcher: Fetch, id: string): Promise<QuoteBuilder> {
  return payload(await fetcher(`${base(id)}/primary`, { method: 'POST' }), 'Could not make it the recommended option.');
}

export async function savePolicy(fetcher: Fetch, body: Partial<PricingPolicy>): Promise<PricingPolicy> {
  return payload(await fetcher('/api/cpq/policy', json('PATCH', body)), 'Could not save the pricing policy.');
}
